import React, { useEffect, useState } from 'react';
import axios from 'axios';

import config from '../config';

const COUNTY_STORAGE_KEY = 'respiteCounty';

const toTelHref = (phone) => `tel:${String(phone || '').replace(/[^\d+]/g, '')}`;

// Shared by the Respite Record page and the Home page summary row.
export default function RespiteHelpModal({ onClose }) {
  const [data, setData] = useState(null);
  const [county, setCounty] = useState(() => localStorage.getItem(COUNTY_STORAGE_KEY) || '');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let canceled = false;

    const load = async () => {
      try {
        const response = await axios.get(`${config.API_BASE_URL}/api/respite-resources`, {
          params: county ? { county } : {},
        });
        if (!canceled) {
          setData(response.data);
          setError('');
        }
      } catch (err) {
        console.error('Fetch respite resources error:', err);
        if (!canceled) {
          setError('We could not load respite resources right now.');
        }
      } finally {
        if (!canceled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      canceled = true;
    };
  }, [county]);

  const handleCountyChange = (value) => {
    setCounty(value);
    if (value) {
      localStorage.setItem(COUNTY_STORAGE_KEY, value);
    } else {
      localStorage.removeItem(COUNTY_STORAGE_KEY);
    }
  };

  const agency = data?.agency;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto vica-scrollbar bg-[#0f2b3a]/55"
      role="dialog"
      aria-modal="true"
      aria-labelledby="respite-help-title"
    >
      <div className="flex min-h-full items-center justify-center px-4 py-6">
        <div className="relative w-full max-w-2xl rounded-[18px] bg-white shadow-[0_24px_70px_rgba(0,52,80,0.28)] border border-[#cfe2ee]">
          <button
            type="button"
            className="absolute right-4 top-4 h-9 w-9 rounded-full border border-white/40 bg-white/15 text-white text-xl leading-none font-bold"
            onClick={onClose}
            aria-label="Close"
            title="Close"
          >
            x
          </button>

          <div className="bg-gradient-to-br from-[#004f79] via-[#006298] to-[#2a8bc7] px-7 py-6 pr-16 text-white">
            <h2 id="respite-help-title" className="m-0 text-2xl font-extrabold">
              Finding Respite Care
            </h2>
            <p className="m-0 mt-2 text-[16px] text-[#e8f6ff]">
              Your Area Agency on Aging can connect you with in-home help, adult day centers, and
              short stays.
            </p>
          </div>

          <div className="px-7 py-6 text-[#0f2b3a]">
            {loading ? (
              <div className="vica-empty-state">Loading resources...</div>
            ) : error ? (
              <div className="rounded-xl border border-[#f3b8b8] bg-[#fde8e8] px-4 py-3 text-sm font-semibold text-[#9b2424]">
                {error}
              </div>
            ) : (
              <>
                {data?.detected?.city && (
                  <p className="m-0 mb-3 text-sm font-semibold text-[#4f6f80]">
                    It looks like you are near {data.detected.city}
                    {data.detected.region ? `, ${data.detected.region}` : ''}. Please confirm your
                    county below.
                  </p>
                )}

                <div className="mb-5">
                  <div className="mb-2 text-sm font-extrabold uppercase tracking-wide text-[#3f6173]">
                    Your county
                  </div>
                  <select
                    className="vica-input"
                    value={county}
                    onChange={(event) => handleCountyChange(event.target.value)}
                  >
                    <option value="">Select your county...</option>
                    {(data?.counties || []).map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                </div>

                {agency ? (
                  <div className="mb-5 rounded-xl border border-[#b7ddf1] bg-[#f0f9ff] px-4 py-4">
                    <div className="text-xs font-extrabold uppercase tracking-wide text-[#3f6173]">
                      Your Area Agency on Aging
                    </div>
                    <div className="mt-1 text-lg font-extrabold text-[#0f3950]">{agency.name}</div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {agency.phone && (
                        <a className="vica-btn vica-btn-md" href={toTelHref(agency.phone)}>
                          Call {agency.phone}
                        </a>
                      )}
                      {agency.toll_free && (
                        <a className="vica-btn-secondary vica-btn-md" href={toTelHref(agency.toll_free)}>
                          Toll free {agency.toll_free}
                        </a>
                      )}
                    </div>
                    {agency.note && (
                      <p className="mb-0 mt-3 text-xs font-semibold text-[#6b8798]">{agency.note}</p>
                    )}
                  </div>
                ) : (
                  <p className="mb-5 mt-0 text-sm font-semibold text-[#6b8798]">
                    Choose your county to see the agency that serves your area.
                  </p>
                )}

                <div className="mb-5">
                  <div className="mb-2 text-sm font-extrabold uppercase tracking-wide text-[#3f6173]">
                    Statewide and national help lines
                  </div>
                  <div className="space-y-2">
                    {(data?.masterLocators || []).map((locator) => (
                      <div
                        key={locator.name}
                        className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[#dceaf2] bg-white px-3 py-2"
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-extrabold text-[#0f3950]">{locator.name}</div>
                          {locator.scope && (
                            <div className="text-xs font-semibold text-[#6b8798]">{locator.scope}</div>
                          )}
                        </div>
                        <div className="flex shrink-0 gap-2">
                          {locator.phone && (
                            <a className="vica-btn vica-btn-sm" href={toTelHref(locator.phone)}>
                              {locator.phone}
                            </a>
                          )}
                          {locator.url && (
                            <a
                              className="vica-btn-secondary vica-btn-sm"
                              href={locator.url}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Website
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {data?.disclaimer && (
                  <p className="mb-0 mt-0 text-xs font-semibold leading-5 text-[#6b8798]">
                    {data.disclaimer}
                  </p>
                )}
              </>
            )}

            <div className="mt-6 flex justify-end">
              <button type="button" className="vica-btn-secondary vica-btn-md" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
